'use client';

import { use, useEffect } from 'react';
import { useRouter, useSearchParams } from '@/lib/app-router';
import { MY_ACCOUNT_ROUTES } from '@/routes/routes.constants';

/**
 * /:id — Default booking redirect (bare location code)
 *
 * Back Office (Garage Settings → Links → "Default Booking Page") and the QR codes printed
 * from it emit `{reservationUrl}/{locationCode}` with no `/d` or `/p` prefix. The legacy
 * reservation site served that form through a wildcard `:id` route (`BookingRedirect
 * isDefault`) that opened the garage with the Hourly/Monthly choice left to the user.
 * Redirects to the reserve page with the garage pre-selected and the mode left to the
 * tenant default. (PB-5238)
 *
 * Preserves optional query params: rateId, coupon, eventId, lat, lng, search_text, occ.
 * Honours the legacy `isMonthly=true|false` param by mapping it to `mode`.
 *
 * ## Invariants:
 * - Must NOT set `mode` unless the URL carried `isMonthly`. An unpinned mode is what makes
 *   the reserve page fall back to `theme.defaultEcommerceBookingOption` and leave the
 *   Hourly/Monthly toggle unlocked (`lockedMode` keys off `hasUrlMode`) — the legacy
 *   "default booking page" behaviour. Pinning a mode here silently turns this into /d or /p.
 * - This is the only root-level dynamic segment. Next.js matches static routes first, so it
 *   never shadows /about, /login, /reserve, etc. Do not widen it to a catch-all.
 */
export default function DefaultBookingRedirectPage(props: { params: Promise<{ id: string }> }) {
  const { id } = use(props.params);
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const url = new URLSearchParams();
    url.set('garageId', id);

    // Legacy default route accepted ?isMonthly=true|false (BookingRedirect.tsx) — map to mode.
    const isMonthly = searchParams.get('isMonthly');
    if (isMonthly === 'true') {
      url.set('mode', 'monthly');
    } else if (isMonthly === 'false') {
      url.set('mode', 'hourly');
    }

    // Forward all supported query params
    for (const key of ['rateId', 'coupon', 'eventId', 'lat', 'lng', 'search_text', 'occ']) {
      const val = searchParams.get(key);
      if (val) url.set(key, val);
    }

    router.replace(`${MY_ACCOUNT_ROUTES.reserve}?${url.toString()}`);
  }, [id, router, searchParams]);

  return (
    <output
      className="tw:flex tw:items-center tw:justify-center tw:min-h-dvh"
      aria-busy="true"
      aria-label="Loading"
    />
  );
}
